import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, ShieldCheck } from "lucide-react";
import { API, COVER_IMAGE } from "../lib/constants";
import { useAdmin } from "../contexts/AdminContext";
import WitnessStandTabs from "../components/WitnessStandTabs";

const TABS = [
  { key: "pending", label: "Pending Review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function WitnessStandModerate() {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [stories, setStories] = useState([]);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const adminToken = localStorage.getItem("admin_token") || "";
    const r = await fetch(`${API}/witness-stand/admin/queue?status=${tab}`, {
      credentials: "include",
      headers: adminToken ? { "X-Admin-Token": adminToken } : {},
    });
    if (!r.ok) { setStories([]); return; }
    const d = await r.json();
    setStories(d.stories || []);
  };

  useEffect(() => { if (isAdmin) load(); }, [tab, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-6" data-testid="moderate-need-admin">
        <div className="max-w-5xl mx-auto">
          <WitnessStandTabs />
          <div className="text-center py-16">
            <p className="text-gray-400 mb-4">Admin access required.</p>
            <button onClick={() => navigate("/witness-stand")} className="px-4 py-2 bg-red-600 rounded-lg text-white font-bold text-sm">Back to Library</button>
          </div>
        </div>
      </div>
    );
  }

  const decide = async (id, action) => {
    let note = "";
    if (action === "reject") {
      note = window.prompt("Reason for rejection (shown to author):");
      if (!note) return;
    }
    setBusy(id);
    try {
      const adminToken = localStorage.getItem("admin_token") || "";
      const r = await fetch(`${API}/witness-stand/admin/${id}/${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(adminToken ? { "X-Admin-Token": adminToken } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ note }),
      });
      if (!r.ok) {
        let msg = "Action failed";
        try { const d = await r.json(); msg = d.detail || msg; } catch {}
        alert(msg);
      }
      await load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="moderate-page">
      <div className="max-w-5xl mx-auto">
        <WitnessStandTabs />
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck size={20} className="text-red-500"/>
          <h1 className="font-cinzel text-2xl md:text-3xl font-bold">Witness Stand · Moderation</h1>
        </div>

        <div className="flex gap-1 mb-6 text-xs">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-2 rounded-lg ${tab === t.key ? "bg-red-600 text-white font-bold" : "bg-white/5 hover:bg-white/10 text-gray-300"}`}
              data-testid={`mod-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {stories.length === 0 ? (
          <p className="text-center text-gray-500 py-12 font-mono text-xs">NOTHING IN QUEUE</p>
        ) : (
          <div className="space-y-4" data-testid="moderate-list">
            {stories.map(s => (
              <div key={s.id} className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden" data-testid={`mod-row-${s.id}`}>
                <div className="flex flex-col sm:flex-row gap-4 p-4">
                  <div className="w-full sm:w-44 h-32 bg-cover bg-center rounded-lg flex-shrink-0" style={{ backgroundImage: `url(${s.cover_url || COVER_IMAGE})` }}/>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] text-red-400 tracking-wider mb-1">{s.genre?.toUpperCase()} · BY {s.author_name?.toUpperCase()} · {s.word_count} WORDS</p>
                    <p className="font-cinzel font-bold text-white text-lg mb-1">{s.title}</p>
                    {s.subtitle && <p className="text-sm text-gray-400 mb-2">{s.subtitle}</p>}
                    <details className="text-xs text-gray-300 mt-2">
                      <summary className="cursor-pointer text-gray-400 hover:text-white">Read full story ({s.word_count} words)</summary>
                      <div className="mt-2 max-h-72 overflow-y-auto p-3 bg-black/40 rounded leading-relaxed whitespace-pre-wrap">{s.content}</div>
                    </details>
                    <div className="flex gap-2 mt-3">
                      {tab !== "approved" && (
                        <button onClick={() => decide(s.id, "approve")} disabled={busy === s.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-xs font-bold text-white disabled:opacity-50"
                          data-testid={`mod-approve-${s.id}`}>
                          <Check size={12}/>Approve
                        </button>
                      )}
                      {tab !== "rejected" && (
                        <button onClick={() => decide(s.id, "reject")} disabled={busy === s.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded bg-red-600/80 hover:bg-red-700 text-xs font-bold text-white disabled:opacity-50"
                          data-testid={`mod-reject-${s.id}`}>
                          <X size={12}/>Reject
                        </button>
                      )}
                      <button onClick={() => navigate(`/witness-stand/story/${s.id}`)}
                        className="px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 text-xs text-gray-200"
                        data-testid={`mod-preview-${s.id}`}>Preview</button>
                    </div>
                    {s.moderator_note && (
                      <p className="text-xs text-amber-300 mt-2 font-mono">NOTE: {s.moderator_note}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
